import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { LuMenu, LuX } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

import SanurWhite from '@/assets/images/logo/sanur-white.webp';
import SupergraphicHeaderLeftBottom from '@/assets/images/supergraphic/header-left-bottom.webp';
import GreyMobileSupergraphic from '@/assets/images/supergraphic/sanur-kv-grey-mobile-header.png';
import { useHeaderData } from '@/shared/hooks/useHeaderData';
import type { HeaderData, HeaderMenuSection } from '@/shared/types/header';

const Header = () => {
  const [showExplore, setShowExplore] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const { headerData } = useHeaderData();

  // Fallback data in case API fails or is loading
  const defaultData: HeaderData = {
    menus: [
      {
        menu: 'About Us',
        route: '/about',
        sub: [
          { title: 'The Sanur', route: '/about' },
          { title: 'Sustainability', route: '/sustainability' },
        ],
      },
      {
        menu: 'Healthcare',
        route: '/healthcare',
        sub: [
          {
            title: 'International Hospital',
            route: '/healthcare/hospital/bali-international-hospital',
          },
          { title: 'Clinics', route: '/healthcare/coe' },
        ],
      },
      {
        menu: 'Wellness',
        route: '/wellness',
        sub: [
          {
            title: 'Etnobothanical Garden',
            route: '/wellness/ethnobotanical-garden',
          },
          { title: 'Spa', route: '/wellness/spa' },
        ],
      },
      {
        menu: 'Accommodations',
        route: '/accomodation',
        sub: [
          {
            title: 'Bali Beach Hotel',
            route: '/accomodation/bali-beach-hotel',
          },
          { title: 'TheMeru', route: '/accomodation/the-meru-sanur' },
        ],
      },
      {
        menu: 'Facilities',
        route: '/venue',
        sub: [
          {
            title: 'Bali Beach Convention',
            route: '/venue/bali-beach-convention-center',
          },
          { title: 'Meeting Rooms', route: '/venue/meeting-rooms' },
          { title: 'Dining', route: '/venue/dining' },
          { title: 'Shop', route: '/venue/shop' },
        ],
      },
    ],
  };

  const data: HeaderData = headerData || defaultData;
  const menus: HeaderMenuSection[] = data.menus;

  // Track stylesheets/style tags injected while the inquiry widget is open
  const widgetStyleNodesRef = useRef<HTMLElement[]>([]);
  const widgetObserverRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    // Safety: disconnect observer on unmount
    return () => {
      widgetObserverRef.current?.disconnect();
    };
  }, []);

  // const openInquiry = () => {
  //   document.body.classList.add('overflow-hidden');
  //   setShowInquiryForm(true);

  //   // Start observing <head> for new CSS nodes added by the widget
  //   widgetStyleNodesRef.current = [];
  //   widgetObserverRef.current?.disconnect();
  //   widgetObserverRef.current = new MutationObserver(mutations => {
  //     for (const m of mutations) {
  //       m.addedNodes.forEach(node => {
  //         if (node.nodeType !== 1) return;
  //         const el = node as HTMLElement;

  //         const isWidgetLink =
  //           el.tagName === 'LINK' &&
  //           (el as HTMLLinkElement).rel === 'stylesheet' &&
  //           ((el as HTMLLinkElement).href ?? '').includes(
  //             'inquiry.demo-thesanur.id'
  //           );

  //         const isNewStyleTag =
  //           el.tagName === 'STYLE' &&
  //           // Heuristic: Tailwind-heavy inline styles often exceed a few KB.
  //           // We can keep it permissive since we only remove what appeared during observation.
  //           (el.textContent?.length || 0) > 256;

  //         if (isWidgetLink || isNewStyleTag) {
  //           widgetStyleNodesRef.current.push(el);
  //         }
  //       });
  //     }
  //   });

  //   widgetObserverRef.current.observe(document.head, {
  //     childList: true,
  //     subtree: true,
  //   });

  //   const script = document.createElement('script');
  //   script.src = 'https://inquiry.demo-thesanur.id/dist/inquiry_form.umd.js'; // hosted UMD URL
  //   script.onload = () => {
  //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //     (window as any).FormWidget?.mount('form-inquiry');
  //     // If you want, you can stop observing after mount settles:
  //     // setTimeout(() => widgetObserverRef.current?.disconnect(), 1000);
  //   };
  //   document.body.appendChild(script);
  // };

  const closeInquiry = () => {
    document.body.classList.remove('overflow-hidden');
    setShowInquiryForm(false);

    // Unmount the widget if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).FormWidget?.unmount?.('form-inquiry');

    // Stop observing and remove captured CSS nodes added during widget lifetime
    widgetObserverRef.current?.disconnect();
    widgetStyleNodesRef.current.forEach(el => el.parentNode?.removeChild(el));
    widgetStyleNodesRef.current = [];

    // Remove the script from the DOM if it exists
    const scripts = document.querySelectorAll(
      'script[src="https://inquiry.demo-thesanur.id/dist/inquiry_form.umd.js"]'
    );
    scripts.forEach(script => script.remove());

    // Close Explore to avoid visual mismatch after CSS change
    setShowExplore(false);

    // Force a quick reflow so media queries recompute immediately
    try {
      window.dispatchEvent(new Event('resize'));
    } catch {
      /* no-op */
    }
  };

  return (
    <Fragment>
      {!showInquiryForm && (
        <div className='absolute top-0 z-50 w-full h-[80px] sm:h-[90px] md:h-[100px] lg:h-[120px] py-[20px] sm:py-[25px] md:py-[30px] flex items-center justify-center'>
          <Link
            to={'/'}
            className='absolute top-0 w-[100px] h-[60px] sm:w-[120px] sm:h-[80px] md:w-[180px] md:h-[120px] lg:w-[226.92px] lg:h-[149.58px]'
            reloadDocument
          >
            <img
              loading='lazy'
              src={SanurWhite}
              alt='Sanur White Logo'
              className='w-full h-full object-contain'
            />
          </Link>

          <div className='relative w-full'>
            <button
              className={`absolute left-4 sm:left-5 md:left-12 lg:left-20 top-0 ${
                showExplore ? 'text-black' : 'text-white'
              } text-[12px] sm:text-[14px] md:text-[16px] lg:text-[20px] flex gap-x-2 sm:gap-x-3 md:gap-x-4 items-center`}
              style={{ fontFamily: 'Figtree' }}
              onClick={() => setShowExplore(v => !v)}
              aria-expanded={showExplore}
              aria-controls='explore-panel'
            >
              {showExplore ? <LuX /> : <LuMenu />}
              <span>{showExplore ? 'CLOSE' : 'EXPLORE'}</span>
            </button>
          </div>

          <div className='pr-[16px] sm:pr-[20px] md:pr-[40px] lg:pr-[80px]'>
            <button
              className='text-white bg-gradient-to-r from-[#F2B214] to-[#E29A00] hover:shadow-amber-100 shadow-md px-[12px] sm:px-[16px] py-[2px] sm:py-[3px] md:py-[4px] text-[9pt] sm:text-[10pt] md:text-[12pt] lg:text-[15pt] font-semibold'
              style={{ fontFamily: 'AngieSansStd' }}
              // onClick={openInquiry}
              onClick={() =>
                window.open(
                  'https://explore.thesanur.id',
                  '_blank',
                  'noopener noreferrer'
                )
              }
            >
              INQUIRY
            </button>
          </div>
        </div>
      )}

      {/* Animated Explore Section */}
      <AnimatePresence>
        {showExplore && (
          <motion.div
            id='explore-panel'
            key='explore-panel'
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height:
                typeof window !== 'undefined' && window.innerWidth < 640
                  ? 480
                  : typeof window !== 'undefined' && window.innerWidth < 768
                    ? 520
                    : typeof window !== 'undefined' && window.innerWidth < 1024
                      ? 580
                      : 620,
              opacity: 1,
            }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className='absolute top-0 z-40 w-full overflow-hidden bg-[#8F8A83] flex justify-center'
          >
            <img
              src={SupergraphicHeaderLeftBottom}
              alt='Supergraphic Header Logo'
              className='hidden md:absolute bottom-0 left-0'
            />
            <img
              src={GreyMobileSupergraphic}
              alt='Supergraphic Header Logo Mobile'
              className='md:hidden absolute bottom-0 right-0'
            />

            {/* Desktop grid */}
            <div
              className='hidden relative w-full h-full pt-[120px] sm:pt-[150px] md:pt-[180px] lg:pt-[200px] px-[20px] sm:px-[40px] md:px-[60px] lg:px-[80px] md:flex justify-between'
              style={{ fontFamily: 'Figtree' }}
            >
              {menus.map((objMenu, idxMenu) => (
                <div
                  key={idxMenu}
                  className='flex flex-col gap-y-[12px] sm:gap-y-[14px] md:gap-y-[16px]'
                >
                  <Link
                    to={objMenu.route}
                    className={`uppercase font-semibold text-[16px] sm:text-[18px] md:text-[20px] ${
                      objMenu.route === window.location.pathname
                        ? 'text-orange-300'
                        : 'text-white'
                    }`}
                    reloadDocument
                  >
                    {objMenu.menu}
                  </Link>
                  {objMenu.sub?.map((subMenu, idxSubMenu) => (
                    <div
                      key={idxSubMenu}
                      className='flex flex-col gap-y-[6px] sm:gap-y-[7px] md:gap-y-[8px]'
                    >
                      <Link
                        to={subMenu.route}
                        className={`text-[14px] sm:text-[16px] md:text-[18px] font-light ${
                          subMenu.route === window.location.pathname
                            ? 'text-orange-300'
                            : 'text-white'
                        }`}
                        reloadDocument
                      >
                        {subMenu.title}
                      </Link>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Mobile grid */}
            <div
              className='md:hidden relative w-full h-full pt-[80px] sm:pt-[100px] pb-[40px] sm:pb-[50px] px-[16px] sm:px-[20px] grid grid-cols-2 gap-x-[12px] sm:gap-x-[16px] gap-y-[24px] sm:gap-y-[28px]'
              style={{ fontFamily: 'Figtree' }}
            >
              {menus.map((objMenu, idxMenu) => (
                <div
                  key={idxMenu}
                  className='flex flex-col gap-y-[12px] sm:gap-y-[16px] w-full max-w-[160px] sm:max-w-[180px]'
                >
                  <Link
                    to={objMenu.route}
                    className={`uppercase font-semibold text-[13px] sm:text-[15px] ${
                      objMenu.route === window.location.pathname
                        ? 'text-orange-300'
                        : 'text-white'
                    }`}
                    reloadDocument
                  >
                    {objMenu.menu}
                  </Link>
                  {objMenu.sub?.map((subMenu, idxSubMenu) => (
                    <div
                      key={idxSubMenu}
                      className='flex flex-col gap-y-[6px] sm:gap-y-[8px]'
                    >
                      <Link
                        to={subMenu.route}
                        className={`text-[11px] sm:text-[12px] font-light ${
                          subMenu.route === window.location.pathname
                            ? 'text-orange-300'
                            : 'text-white'
                        }`}
                        reloadDocument
                      >
                        {subMenu.title}
                      </Link>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showInquiryForm && (
        <div className='fixed inset-0 z-50 bg-[#F1F1E9] overflow-y-auto'>
          <div id='form-inquiry' className='w-full h-full min-h-screen'></div>
          <LuX
            className='absolute top-3 right-4 sm:top-4 sm:right-6 md:top-4 md:right-8 text-black text-[16pt] sm:text-[18pt] md:text-[20pt] cursor-pointer z-10'
            onClick={closeInquiry}
            aria-label='Close inquiry form'
            role='button'
          />
        </div>
      )}
    </Fragment>
  );
};

export default Header;
